// While you were away (Features 4, onboarding part 6): after three days or more since the account last opened the
// board, one short card in the corner home: what agents did (Board activity), what waits on the user (a plan's
// questions, a Button's new words to OK, an agent asking for them), and the plan steps that moved, each with one click
// to it. Dismissed in one click, on every device. Shorter absences keep the corner's small "Since" pill
// (src/chrome/Corner.tsx), never both. Every count is the board's own records; a list the snapshot cuts short says so.

import { useMemo, useState } from "react";
import { ChevronRight, X } from "lucide-react";
import { kindOf } from "../../shared/boardKinds";
import { isDismissed } from "../../shared/dismiss";
import { pageTitle } from "../../shared/entries";
import { logOf, needsYou, partsOf, questionOf, statusLine, STATUS_NAME, type PlanStatus } from "../../shared/plan";
import type { ActivityTo, Entry, WidgetItem } from "../../shared/types";
import { Who } from "../chrome/agentMarks";
import { cx } from "../lib/util";
import { openNotice } from "../state/notices";
import { writeOnboarding, useOnboarding } from "../state/onboarding";
import { get, useStore, type AppState } from "../state/store";
import { suggestedTask } from "../widgets/action";
import { useHoldTips } from "./cards";
import "./chrome.css";

/** Away this long, and the card shows in place of the Since pill. */
export const AWAY_MS = 3 * 86_400_000;
/** What the snapshot holds of Board activity (worker/workspace.ts activity(100)). */
const ACTIVITY_CAP = 100;

interface Row {
  key: string;
  title: string;
  meta: string;
  who?: string;
  status?: PlanStatus;
  go: () => void;
}

const goTo = (to: ActivityTo) => void import("../state/goto").then((m) => m.goTo(to));
const dayWords = (t: number) => new Date(t).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
const shortDay = (t: number) => new Date(t).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });

/**
 * Whether this visit is the first in three days or more, the card has not been put away since, and it has something to
 * say: an absence with nothing to report leaves the corner to What's new or the Since pill.
 */
export function useAway(): number | null {
  const since = useStore((s) => s.lastVisitAt);
  const put = useOnboarding((o) => o.visit ?? 0);
  const away = !!since && Date.now() - since >= AWAY_MS && put <= since;
  const says = useStore((s) => (away && since ? saysSomething(awayOf(s, since)) : false));
  return away && says ? since : null;
}

/** Whether the card has anything to show. */
const saysSomething = (a: ReturnType<typeof awayOf>) => a.acts > 0 || a.waits.length > 0 || a.moved.length > 0;

/** What happened while the user was away, from the board's own records. */
export function awayOf(s: AppState, since: number) {
  const boardName = (id: string | null | undefined) => s.boards.find((b) => b.id === id)?.name ?? null;
  // what agents did: Board activity's lines by an agent since then, newest first
  const acts = s.activity.filter((a) => a.t > since && a.who !== "You" && a.who !== "API" && a.kind !== "system" && !a.coworker);
  const cut = s.activity.length >= ACTIVITY_CAP && s.activity.every((a) => a.t > since);
  const agents = [...new Set(acts.map((a) => a.who))];
  const did: Row[] = acts.slice(0, 3).map((a) => {
    const to = a.to ?? (a.itemId && a.boardId ? { board: a.boardId, item: a.itemId } : a.boardId ? { board: a.boardId } : null);
    return { key: a.id, who: a.who, title: a.title, meta: [boardName(a.boardId), shortDay(a.t)].filter(Boolean).join(" · "), go: () => to && goTo(to) };
  });
  // what waits on the user: plans' open questions, Buttons' new words, an agent asking for them
  const waits: Row[] = [];
  const d = s.workspace?.dismissed;
  for (const b of s.boards) {
    if (kindOf(b) !== "plan") continue;
    const entries = Object.values(s.entries).filter((e) => e.boardId === b.id);
    for (const q of needsYou(entries).questions) {
      if (isDismissed(d, `q:${q.id}`)) continue;
      waits.push({ key: q.id, title: questionOf(q).text, meta: `A question on ${b.name}`, go: () => goTo({ board: b.id, entry: q.id }) });
    }
  }
  for (const i of Object.values(s.items)) {
    if (i.kind !== "widget" || i.type !== "action" || s.archived.some((b) => b.id === i.boardId)) continue;
    if (!suggestedTask(i as WidgetItem)) continue;
    waits.push({ key: i.id, title: `New words for “${i.title || "Button"}” to OK`, meta: boardName(i.boardId) ?? "", go: () => goTo({ board: i.boardId, item: i.id }) });
  }
  for (const n of s.notices) {
    if (n.kind !== "agent" || n.read || isDismissed(d, `n:${n.id}`)) continue;
    waits.push({ key: n.id, title: n.title, meta: [boardName(n.board), shortDay(n.t)].filter(Boolean).join(" · "), go: () => openNotice(n) });
  }
  // plan steps that moved: each step's newest change of status since then
  const moved: Row[] = [];
  for (const b of s.boards) {
    if (kindOf(b) !== "plan") continue;
    const pages = Object.values(s.entries).filter((e): e is Entry => e.boardId === b.id && e.t === "page");
    for (const { page } of partsOf(pages)) {
      const statuses = logOf(page)
        .filter((l) => l.kind === "status")
        .map((l) => ({ at: l.at, s: statusLine(l.text) }))
        .filter((x) => x.s);
      const last = statuses.filter((x) => x.at > since).pop();
      // a step made while away, still as planned, has not moved
      if (last?.s && !(last.s.status === "planned" && statuses.indexOf(last) === 0)) moved.push({ key: page.id, title: pageTitle(page), meta: `${b.name} · ${shortDay(last.at)}`, status: last.s.status, go: () => goTo({ board: b.id, entry: page.id }) });
    }
  }
  return { acts: acts.length, cut, agents, did, waits, moved };
}

export function Away({ since }: { since: number }) {
  const activity = useStore((s) => s.activity);
  const entries = useStore((s) => s.entries);
  const items = useStore((s) => s.items);
  const notices = useStore((s) => s.notices);
  const boards = useStore((s) => s.boards);
  const [gone, setGone] = useState(false);
  const a = useMemo(() => awayOf(get(), since), [since, activity, entries, items, notices, boards]); // eslint-disable-line react-hooks/exhaustive-deps
  if (gone || !saysSomething(a)) return null;
  return <AwayCard since={since} a={a} onDismiss={() => setGone(true)} />;
}

/** The card itself, which holds the first-time tips back while it is on screen. */
function AwayCard({ since, a, onDismiss }: { since: number; a: ReturnType<typeof awayOf>; onDismiss: () => void }) {
  useHoldTips();
  const dismiss = () => {
    onDismiss();
    // put away on every device: the next visit starts from now
    writeOnboarding({ visit: Date.now() }, "While you were away seen");
  };
  const n = `${a.acts}${a.cut ? "+" : ""}`;
  const who = a.agents.length === 1 ? a.agents[0] : a.agents.length ? "Your agents" : "";
  return (
    <section className="away-card island" data-away aria-label="While you were away">
      <div className="away-head">
        <div>
          <h3>While you were away</h3>
          <span className="away-since">Since {dayWords(since)}</span>
        </div>
        <button className="icon-btn sm" aria-label="Dismiss" data-tip="Dismiss" data-away-dismiss onClick={dismiss}>
          <X size={14} />
        </button>
      </div>
      {a.acts > 0 && (
        <div className="away-part" data-away-part="did">
          <h4>
            {who} made {n} change{a.acts === 1 && !a.cut ? "" : "s"}
          </h4>
          {a.did.map((r) => (
            <AwayRow key={r.key} r={r} />
          ))}
        </div>
      )}
      {a.waits.length > 0 && (
        <div className="away-part" data-away-part="waits">
          <h4>
            {a.waits.length} waiting on you
          </h4>
          {a.waits.slice(0, 3).map((r) => (
            <AwayRow key={r.key} r={r} />
          ))}
          {a.waits.length > 3 && <p className="away-more">and {a.waits.length - 3} more in the bell</p>}
        </div>
      )}
      {a.moved.length > 0 && (
        <div className="away-part" data-away-part="moved">
          <h4>
            {a.moved.length} plan step{a.moved.length === 1 ? "" : "s"} moved
          </h4>
          {a.moved.slice(0, 3).map((r) => (
            <AwayRow key={r.key} r={r} />
          ))}
          {a.moved.length > 3 && <p className="away-more">and {a.moved.length - 3} more</p>}
        </div>
      )}
    </section>
  );
}

function AwayRow({ r }: { r: Row }) {
  return (
    <button className="away-row" onClick={r.go}>
      {r.who && <Who name={r.who} />}
      <span className="away-row-words">
        <b>{r.title}</b>
        <span>{r.meta}</span>
      </span>
      {r.status ? <span className={cx("away-status", `is-${r.status}`)}>{STATUS_NAME[r.status]}</span> : <ChevronRight size={13} className="away-chev" aria-hidden />}
    </button>
  );
}
